import { InPlaceEditorProps, Labeled, NumberField, NumberInput, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";
import { InputAdornment } from "@mui/material";
import { validatePercentageRange } from "@/utils/validators/inPlaceValidators";

/**
 * In-place editor for percentage fields.
 *
 * Displays a formatted percentage (e.g., "15%") and allows editing as a whole number.
 * Automatically converts between display format (15) and storage format (0.15).
 * Wrapped in a {@link Labeled} component to optionally show a field label.
 *
 * @param source - The field name to bind to
 * @param label - Optional label displayed above the input
 * @param readOnly - If true, disables editing and shows display only
 * @param rest - Additional props passed to {@link InPlaceEditor} and {@link NumberInput}
 */
const InPlacePercentageEditor = ({
  source,
  label,
  readOnly = false,
  validate,
  ...rest
}: {
  source: string;
  label?: string;
  readOnly?: boolean;
  validate?: Validator | Validator[];
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  // Compose built-in percentage range validation with any user-supplied validators
  const effectiveValidate: Validator | Validator[] = validate
    ? [validatePercentageRange, ...(Array.isArray(validate) ? validate : [validate])]
    : validatePercentageRange;
  // Read-only mode: just show the formatted percentage
  if (readOnly) {
    return (
      <Labeled label={label}>
        <NumberField
          source={source}
          variant="body1"
          sx={{ display: "block", mb: 0.5 }}
          emptyText="-"
          options={{
            style: "percent",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }}
        />
      </Labeled>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <NumberInput
            {...rest}
            source={source}
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={effectiveValidate ? undefined : false}
            validate={effectiveValidate}
            autoFocus
            // Convert decimal to percentage for display in input
            format={(value) => (value !== null ? parseFloat((value * 100).toPrecision(10)) : "")}
            // Convert percentage to decimal for storage
            parse={(value) => (value !== "" ? parseFloat((parseFloat(value) / 100).toPrecision(10)) : null)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        <NumberField
          source={source}
          variant="body1"
          sx={{ display: "block", mb: 0.5 }}
          emptyText="-"
          options={{
            style: "percent",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }}
        />
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlacePercentageEditor;
